import { ElasticSearchFacet, ElasticSearchResponse } from '@interfaces/api/responses/elasticSearch';
import { AvailableCountryISOCodes, Country } from '@interfaces/models/country';
import { getCountryNameFromCountryISOCode } from '@helpers/utils/general';
import { ElasticSearch, ElasticSearchFacetsFields } from '@interfaces/models/elasticSearch';
import useFacetSearch, { SearchFacetsInput } from '@hooks/filters/use-facet-search';
import { facetToFilterRename } from '@maps/catalog/search-product-facets-filters-maps';
import mapModels from '@maps/catalog/map-models';
import { SortByMethod, SortTypes } from '@interfaces/models/catalogFilter';
import { useEffect, useRef, useState } from 'react';
import { sortFacetItems } from '@helpers/utils/catalog/catalog-sorting-utils';
import { usePreferences } from '@context/preferences.context';
import useLocalizationQuery from '@hooks/localization/use-localization';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { mapCountryToTheSizeType } from '@helpers/utils/catalog/catalog-url-helpers';

const SEARCH_DEBOUNCE_MS = 300;

const handleCountryFacetsChange = ({
  countryFacets,
  countryList,
  searchTerm,
}: {
  countryFacets: ElasticSearchFacet[];
  countryList: Country[];
  searchTerm: string;
}) => {
  // map country ISO code to country name
  const countryFacetData = countryFacets.reduce((accum: ElasticSearchFacet[], facet: ElasticSearchFacet) => {
    const countryName = getCountryNameFromCountryISOCode(countryList, facet.name as AvailableCountryISOCodes);
    if (countryName) {
      return [
        ...accum,
        {
          ...facet,
          name: countryName,
        },
      ];
    }
    return accum;
  }, []);
  // for country search we do a simple client-side filter
  if (searchTerm) {
    return [...countryFacetData].filter((facet) => facet.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  }
  return [...countryFacetData];
};

const handleBooleanFacetsChange = (booleanFacets: ElasticSearchFacet[]) => {
  const facetData = booleanFacets?.find((facet) => facet.id === '1');
  return facetData ? [facetData] : null;
};

const handleLocalCountriesFacetsChange = (
  localCountriesFacets: ElasticSearchFacet[],
  country: AvailableCountryISOCodes,
) => {
  const userCountryFacetData = localCountriesFacets?.find((facet) => facet.id === country);
  return userCountryFacetData ? [userCountryFacetData] : null;
};

const handleSearchableFacetsChange = async ({
  filterGroup,
  facets,
  searchTerm,
  filters,
  searchFacets,
}: {
  filterGroup: ElasticSearchFacetsFields;
  facets: ElasticSearchResponse['facets']['fields'];
  searchTerm: string;
  filters: ElasticSearch['filters'];
  searchFacets: (input: SearchFacetsInput) => Promise<ElasticSearchFacet[]>;
}) => {
  // remove model / brand filter when we're searching for models / brands respectively (DSXP-461)
  const keyToRemove = facetToFilterRename(filterGroup);
  const { [keyToRemove]: key, ...filtersForPayload } = filters;
  if (!searchTerm) {
    return filterGroup === 'model' ? [...mapModels(facets.model)] : [...facets[filterGroup]];
  }

  try {
    return await searchFacets({ searchTerm, filtersForPayload });
  } catch (error) {
    return [...facets[filterGroup]];
  }
};

const getSortByMethod = ({
  filterGroupSortBy,
  country,
  filterGroup,
  searchTerm,
}: {
  filterGroupSortBy?: Partial<SortTypes> | SortByMethod;
  country: AvailableCountryISOCodes;
  filterGroup: ElasticSearchFacetsFields;
  searchTerm: string;
}) => {
  if (['brand', 'model'].includes(filterGroup) && !!searchTerm) {
    return 'sortByCount';
  }
  return typeof filterGroupSortBy === 'object'
    ? filterGroupSortBy[mapCountryToTheSizeType(country)] ?? filterGroupSortBy.default
    : filterGroupSortBy;
};

const useFilterListFacets = ({
  filterGroupSortBy,
  filterGroup,
  selectedStringListFilters,
}: {
  filterGroupSortBy?: Partial<SortTypes> | SortByMethod;
  filterGroup: ElasticSearchFacetsFields;
  selectedStringListFilters?: string[];
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredAndSortedFacets, setFilteredAndSortedFacets] = useState<ElasticSearchFacet[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout>(null);

  const { country } = usePreferences();
  const { countryList } = useLocalizationQuery();
  const facets = useContextSelector(CatalogContext, (v) => v.facets);
  const filters = useContextSelector(CatalogContext, (v) => v.filters);

  // featureFlag: for US-international and KR region we need to sort data for size1, size20, size22, size21 and size 12 by sortClothingSizes - data in filter configuration file
  const sortByMethod: SortByMethod = getSortByMethod({ filterGroupSortBy, country, filterGroup, searchTerm });

  const { searchFacets } = useFacetSearch({ filterGroup });

  const debouncedSearchFacets = (input: SearchFacetsInput): Promise<ElasticSearchFacet[] | null> => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    return new Promise((resolve) => {
      searchTimeoutRef.current = setTimeout(async () => {
        searchTimeoutRef.current = null;
        const result = await searchFacets(input);
        resolve(result);
      }, SEARCH_DEBOUNCE_MS);
    });
  };

  const handleFacetsChange = async (): Promise<ElasticSearchFacet[] | null> => {
    switch (filterGroup) {
      case 'country':
        return handleCountryFacetsChange({ countryFacets: facets[filterGroup], countryList, searchTerm });
      case 'stock':
      case 'dutyFree':
      case 'dealEligible':
        return handleBooleanFacetsChange(facets[filterGroup]);
      case 'localCountries':
        return handleLocalCountriesFacetsChange(facets[filterGroup], country);
      case 'model':
      case 'brand':
        return handleSearchableFacetsChange({
          filterGroup,
          facets,
          searchTerm,
          filters,
          searchFacets: debouncedSearchFacets,
        });
      default:
        return [...(facets[filterGroup] ?? [])];
    }
  };

  useEffect(() => {
    // Clear search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!facets) {
      return;
    }

    handleFacetsChange().then((unfilteredFacets) => {
      if (!unfilteredFacets) {
        return;
      }

      // sorting facet values based on appropriate sortBy function in configuration file
      const sortedFacets = unfilteredFacets.sort((a, b) => sortFacetItems(sortByMethod, a, b));

      // sorting country facet to put user country on first place
      if (filterGroup == 'country' && sortedFacets.findIndex((filter) => filter.id === country) > 0) {
        sortedFacets.sort((a, b) => {
          return a.id === country ? -1 : b.id === country ? 1 : 0;
        });
      }

      // when users views the full list of filters, we want to show the selected filters first
      if (!searchTerm && selectedStringListFilters && selectedStringListFilters.length > 0) {
        const selectedFilterSet = new Set(selectedStringListFilters);
        sortedFacets.sort((a, b) => {
          const aSelected = selectedFilterSet.has(a.id);
          const bSelected = selectedFilterSet.has(b.id);

          if (aSelected && !bSelected) {
            return -1;
          }
          if (!aSelected && bSelected) {
            return 1;
          }
          return 0;
        });
      }
      setFilteredAndSortedFacets(sortedFacets);
    });
  }, [searchTerm, facets, filterGroup]);

  // Reset search when facets change
  useEffect(() => {
    setSearchTerm('');
  }, [facets]);

  return { searchTerm, setSearchTerm, filteredAndSortedFacets, setFilteredAndSortedFacets };
};

export default useFilterListFacets;
